import {
  addV6BootLoaderEvent,
  SupplierInfo,
  v6Config,
  v6ConfigActiveVersionStr,
  v6SupportsVersion,
  v6VersionMap
} from '../../webmodule-common/interop/interfaces/v6-config';
import { ApiError, ProblemTypes } from '../../webmodule-common/interop/problem-details';
import { applyDebugInformation } from './debug';
import { askOption, information } from '../../webmodule-common/other/ui/modal-option';
import { bindApplicationFeatures } from '../../webmodule-common/other/bind-application-features';
import { BlobApiImpl } from '../api/blob-api-impl';
import { changeLanguage, globalLanguage, interpolate } from '../../localization/global';
import {
  clearCurrentUserFromSession,
  saveCurrentUserIntoSession,
  verifySession
} from '../../webmodule-common/other/user-session-verifier';
import { ClientCache } from './cache-impl/client-cache';
import { ClientPrimaryContactCache } from './cache-impl/client-primarycontact-cache';
import { ContactReferenceCache } from './cache-impl/contact-reference-cache';
import {
  createFranchiseeQuoteProviderData,
  validateAndUpdateFranchiseeQuoteProviderData
} from './quotes/data/franchisee-quote-provider-data';
import { currentUserClaims } from '../../webmodule-common/other/currentuser-claims';
import {
  DealerApiCommunications,
  redirectToLoginPage,
  setDealerTokenProvider
} from '../../webmodule-common/other/api/dealer-api-communications';
import { DealerClientAgentApi } from '../api/clientagent-api-implementation';
import { DealerClientApi } from '../api/client-api-implementation';
import { DealerFranchiseeApi } from '../api/franchisee-api-implementation';
import { dealerVerifyUser, performPageLoadLoginVerifications } from '../api/dealer-pageload-verifications';
import { DefaultLangBuilder } from '../../localization/default-lang-builder';
import { defaultLanguage } from '../../localization/consts';
import { DevelopmentError, showDevelopmentError } from '../../webmodule-common/other/development-error';
import { displayAllError } from '../../webmodule-common/other/ui/modal-errorhandler';
import { displaySupplierTACNotApprovedMsg, GlobalSupplierNotification } from '../v6config/supplier-services';
import { emptyGuid } from '../../webmodule-common/other/api/guid';
import { EventCancellation } from '../../webmodule-common/other/general/staged-event';
import { FabricationJobApiImplementation } from '../api/fabrication-job-api-implementation';
import { FabricationPurchaseOrderApiImplementation } from '../api/fabrication-purchase-order-api-implementation';
import {
  CustomerCodeFlushedEvent,
  frameconfigForceFlushCaches,
  PendingCustomerCodeFlushes
} from './v6-customer-code-flushed';
import { FranchiseeClientApiImplementation } from '../api/franchisee-client-api-implementation';
import { getApi, setApiInjector } from '../../webmodule-common/other/api/api-injector';
import { getApiFactory, setApiFactory } from '../api/api-injector';
import { getCurrentUser, setAfterApplyUserEvent } from '../../webmodule-common/other/api/current-user';
import { getQuoteSuppliers, setQuoteSupplierProviderCallback } from '../quotes/quote-service';
import { getUserLock } from '../../webmodule-common/other/api/optimistic-user-lock';
import { GlobalNotificationManager } from '../../webmodule-common/other/ui/icons/icon-notification-signal';
import { initOfflineOverlaySystem } from '../../webmodule-common/other/ui/offline-overlay';
import { initPageResumeHandler } from './common/page-resume-integration';
import { intializeLocalizationServices } from '../../localization/initialize-services';
import { isDevSite } from '../../webmodule-common/other/domain';
import { LangApiImplementation } from '../api/lang-api-implementation';
import { LangApplicationInitialization } from '../../localization/scripts/dealer-franchisee/application-initialization-lang';
import { LanguageResources } from '../../localization/language-resources';
import { noSqlDb } from '../../webmodule-common/database/no-sql-db';
import { PaymentProfileApiImplementation } from '../api/payment-profile-api-implementation';
import { PaymentProfileCache } from './cache-impl/payment-profile-cache';
import { ProjectApiImplementation } from '../api/project-api-implementation';
import { ProjectCache, ProjectPermanentDocumentCache } from './cache-impl/project-cache';
import { ProjectResourceLinkCache } from './cache-impl/project-resource-link';
import { PurchaseOrderApiImplementation } from '../api/purchase-order-api-implementation';
import { PurchaseOrderCache } from './cache-impl/purchase-order-cache';
import { QuoteApiImpl } from '../api/quote-api-impl';
import { QuoteCache } from './cache-impl/quote-cache';
import { QuoteItemConversationCache } from './cache-impl/quoteitemconversation-cache';
import { registerBranchQuoteDefaultsSSEListener } from './quotes/data/v6/branch-quote-defaults';
import { registerComponents } from '../../webmodule-common/components/src/components/register-components';
import { registerComponentsLocal } from '../../webmodule-common/other/ui/templatecontrols/component-registry';
import { registerLangFile } from '../../webmodule-common/other/language/lang';
import { registerQuoteCopyCompletionMonitor } from './quotes/data/quote-copy-completion-monitor';
import { registerTechnicalDictionary } from '../../webmodule-common/other/language/technical-dictionary';
import { ResourcesCache } from './cache-impl/resources-cache';
import { responseHandler } from '../api/api-response-handler';
import { runGlobalInit } from '../../webmodule-common/other/app-global-init';
import { setCacheRegistry } from './cache-impl/cache-registry';
import { setErrorDialogEventHandler, showError } from '../../webmodule-common/other/ui/show-error';
import { setLocalizeInjector } from '../../webmodule-common/components/src/common/localize-injector';
import {
  setQuoteProviderDataFactory,
  setQuoteProviderDataValidateAndUpgradeEvent
} from '../quotes/data/quote-provider-data';
import { setSupplierInformationCache } from '../quotes/data/quoteSupplierProvider';
import { stagedExecution, WMEventSourceClient } from '../../webmodule-common/other/general/event-source-client';
import { SupplierApiImplementation } from '../api/supplier-api-implementation';
import { SupplierTermsAndConditionsModal } from './supplier/terms-and-conditions-modal';
import { TermsAndConditionsApiImplementation } from '../api/terms-and-conditions-api-implementation';
import { TermsConditionsCache } from './cache-impl/terms-conditions-cache';
import { UserApiImplementation } from '../api/user-api-implementation';
import { userDataStore } from './common/current-user-data-store';
import { UserProfileCache } from './cache-impl/user-profile-cache';
import { v6BootLoader, v6bootloaderStale } from '../v6config/v6config';

import { WMEventSource } from '../api/event-source';
import type { ConnectedState } from '../api/dealer-api-interface-franchisee';
import type { EventSnippet } from '../../webmodule-common/interop/types/misc';
import type { QuoteSupplier } from '../quotes/quote-service';

const expiresIn = {
  oneDay: 60 * 60 * 24,
  oneWeek: 60 * 60 * 24 * 7,
  oneMonth: 60 * 60 * 24 * 30,
  nextDay: -100
};

async function processSupplierTAC(suppliers: QuoteSupplier[]) {
  for (let i = 0; i < suppliers.length; i++) {
    const supplier = suppliers[i];
    const tacApproved = userDataStore.supplierTACApproved(supplier.supplierId);
    if (!tacApproved) {
      const claims = currentUserClaims();
      if (claims.isAgent || claims.isCynclyStaff || claims.trueClaim('non-tenant-user'))
        await displaySupplierTACNotApprovedMsg(supplier);
      else await approveTACforSupplier(supplier);
    }
  }
}

export let _v6SupplierVersion = '';
export let _v6FrameConfigVersion = '';
export let _v6FrameConfigApiVersion = '';

function applyQuoteSupplierProviderCallback() {
  setQuoteSupplierProviderCallback(async (refreshList: boolean) => {
    //BOOTLOADER MUST BE RUN BEFORE THIS FUNCTION

    const v6Suppliers = (await v6Config().suppliers(refreshList)) ?? [];

    _v6SupplierVersion = v6Suppliers.length === 1 ? v6Suppliers[0].version : '';
    _v6FrameConfigVersion = v6ConfigActiveVersionStr();
    _v6FrameConfigApiVersion = globalThis.dealerConfiguration.v6apiBuildNumberDisplay;

    const results = v6Suppliers.map(s => {
      const qs: QuoteSupplier = {
        supplierId: s.id,
        description: s.description,
        online: s.online,
        offlineCause: s.offlineCause,
        offlineMessage: s.offlineMessage
      };
      return qs;
    });
    results.forEach(si => {
      GlobalSupplierNotification.getInstance(si.supplierId).setState(si);
    });
    return results;
  });
}

const bootloaderStaleEvent = (gsn: GlobalSupplierNotification) => {
  if (!gsn.online) v6bootloaderStale();
  else
    v6BootLoader().then(value => {
      console.log(`V6Bootloader: ${value}`);
    });
};

const afterV6BootLoader = async (): Promise<void> => {
  if (!userDataStore.loaded) throw new DevelopmentError('cant perform bootloading without user data');
  const supplierLocalizationKey = (id: string) => `v6:supplier:${id}:localization-instance-id`;
  const supplierLanguageKey = (id: string, version: string) => `lang:v6:${id}:${version}`;
  const flushV6Languages = async (suppliers: SupplierInfo[]): Promise<boolean> => {
    if (v6SupportsVersion(v6VersionMap.hasLocalization)) {
      if (!isDevSite()) return false;

      //this pre-scan block is used to ensure we dont waste time. scan the current instance id, against the
      //old instance id, and if they are different, flush the cache
      for (const supplier of suppliers) {
        const details = await noSqlDb.getItem<SupplierInfo>(supplierLocalizationKey(supplier.id));
        if (details && details.instanceId !== supplier.instanceId) {
          //this is used in the dev environment to flush language data
          await noSqlDb.delItem(supplierLocalizationKey(supplier.id));
          await noSqlDb.delGrabItem(supplierLanguageKey(supplier.id, supplier.version));
        }
      }
      //now we swap out the languages with new ones from the new cache.
      await updateV6DbLanguages(suppliers);
      return true;
    }
    return false;
  };
  const updateV6DbLanguages = async (v6Suppliers: SupplierInfo[]) => {
    for (const supplier of v6Suppliers) {
      if (!supplier.online) continue;
      const version = supplier.version;

      //this is used in the dev environment to flush language data
      await noSqlDb.setItem(supplierLocalizationKey(supplier.id), supplier);
      try {
        const v6DbLanguages = await noSqlDb.grab(
          supplierLanguageKey(supplier.id, version),
          async () => {
            return await getApiFactory().lang().getV6DbLangFile(supplier.id, version);
          },
          expiresIn.oneMonth
        );
        globalLanguage.languageServer.getLanguages().forEach(l => {
          const v6Lang = v6DbLanguages?.find(x => x.language === l);
          if (v6Lang) {
            globalLanguage.languageServer.getLanguage(l).swap(LanguageResources.FromLanguageItemData(v6Lang));
          }
        });
      } catch (e) {
        console.error('Could not load v6 supplier languages for ' + supplier.description, e);
      }
    }
  };

  globalThis.v6config = globalThis.v6config ?? {};

  //this is a global event hook that is called when the suppliers are reloaded by the frameconfig engine.
  globalThis.v6config.supplierReloaded = async (suppliers: SupplierInfo[]) => {
    console.log('V6Config: Supplier Reloaded');
    if (v6SupportsVersion(v6VersionMap.hasLocalization)) {
      if (!(await flushV6Languages(suppliers)))
        //now we swap out the languages with new ones from the new cache.
        await updateV6DbLanguages(suppliers);
    }
  };

  applyQuoteSupplierProviderCallback();

  await userDataStore.setV6OverrideKey();
  const suppliers = await getQuoteSuppliers(true);
  suppliers.forEach(s => {
    GlobalSupplierNotification.getInstance(s.supplierId).addEventListenter(bootloaderStaleEvent);
  });

  //we need to get the correct language information loaded for the frameconfig and v6 suppliers
  if (v6SupportsVersion(v6VersionMap.hasLocalization)) {
    const v6Suppliers = (await v6Config().suppliers()) ?? [];
    const frameconfigLanguages = await noSqlDb.grab(
      `lang:frameconfig:${_v6FrameConfigVersion}`,
      async () => {
        return await getApiFactory().lang().getFrameConfigLangFile(_v6FrameConfigVersion);
      },
      expiresIn.oneMonth
    );

    const defaultFrameConfigLang = globalLanguage.languageServer
      .getLanguage(defaultLanguage)
      .resources.find(r => r.name === 'frameconfig');

    globalLanguage.languageServer.getLanguages().forEach(l => {
      const fcLang = frameconfigLanguages?.find(x => x.language === l);
      const resources = fcLang ? LanguageResources.FromLanguageItemData(fcLang) : defaultFrameConfigLang;
      if (resources) globalLanguage.languageServer.getLanguage(l).swap(resources);
    });
    if (!(await flushV6Languages(v6Suppliers))) await updateV6DbLanguages(v6Suppliers);
  }
  changeLanguage();
  await processSupplierTAC(suppliers);
  currentSupplierNames = suppliers.map(x => `[${x.description}]`).join(', ');
};

export let currentSupplierNames = '';

async function dealerPrepUserData(updateNote: (msg: string) => void) {
  try {
    updateNote('Initialize Frame Configuration Service');
    //bootloader has already been run by the currentuserdatastore
    //
    // @SUPOFF-BOOT-005: a first visit with the supplier offline and nothing cached has no version
    // to fall back on and genuinely cannot start - but it must not turn into a reload loop. Tell
    // the dealer plainly and let them retry (which re-checks live rather than reloading the page).
    while (!(await v6BootLoader())) {
      await askOption(
        LangApplicationInitialization.frameConfigSystemCouldNotBeLoaded(),
        [{ caption: LangApplicationInitialization.tryAgain(), value: 'retry' }],
        LangApplicationInitialization.frameConfigServicesOffline()
      );
      v6bootloaderStale();
    }
    //we want to enforce that the system the user connects to is fully up to date. this is a
    //simple call to the v6 systems to check their version, and will ensure the system resets info
    //as needed. This is also required for the quotes page, so we know if we are a multi supplier view or not
    updateNote('Fetching Supplier Information');
  } catch (e) {
    console.log('could not load suppliers' + (e as any).toString());
  }
}

function dealerBuildApplicationBindings() {
  setLocalizeInjector({
    interpolate: (id: string, data?: Record<string, any>) => {
      return interpolate(id, data);
    }
  });
  intializeLocalizationServices(
    globalThis.dealerConfiguration.languages,
    globalThis.dealerConfiguration.glossary,
    globalThis.dealerConfiguration.localizationFiles,
    () => DefaultLangBuilder.default()
  );

  //deprecated
  registerTechnicalDictionary(globalThis.dealerConfiguration.dictItems);
  //deprecated
  registerLangFile(globalThis.dealerConfiguration.langFile);

  applyDebugInformation();
  registerComponents();
  registerComponentsLocal();
  bindApplicationFeatures();
  setAfterApplyUserEvent(connectUserAfterLogin);
  registerEventSourceClients();
  setSupplierInformationCache({
    getSupplierDisplayName: async (supplierId: string) => {
      const suppliers = await getQuoteSuppliers(false);
      return suppliers.find(x => x.supplierId === supplierId)?.description ?? LangApplicationInitialization.supplier();
    }
  });
  setDealerTokenProvider(() => getUserLock());
  const errorDialogEventHandler = async (item: Error | ApiError, title: EventSnippet) =>
    await displayAllError(title, item);
  setErrorDialogEventHandler(errorDialogEventHandler);

  //Bind data provider information inot the generic
  setQuoteProviderDataFactory(createFranchiseeQuoteProviderData);
  setQuoteProviderDataValidateAndUpgradeEvent(validateAndUpdateFranchiseeQuoteProviderData);

  setApiFactory({
    quote: () => new QuoteApiImpl(getApi()),
    blob: () => new BlobApiImpl(getApi()),
    client: () => new DealerClientApi(getApi()),
    project: () => new ProjectApiImplementation(getApi()),
    franchisee: () => new DealerFranchiseeApi(getApi()),
    paymentProfile: () => new PaymentProfileApiImplementation(getApi()),
    fabricationJob: () => new FabricationJobApiImplementation(getApi()),
    fabricationPurchaseOrder: () => new FabricationPurchaseOrderApiImplementation(getApi()),
    purchaseOrder: () => new PurchaseOrderApiImplementation(getApi()),
    supplier: () => new SupplierApiImplementation(getApi()),
    clientAgent: () => new DealerClientAgentApi(getApi()),
    user: () => new UserApiImplementation(getApi()),
    termsAndConditions: () => new TermsAndConditionsApiImplementation(getApi()),
    lang: () => new LangApiImplementation(getApi()),
    franchiseeClient: () => new FranchiseeClientApiImplementation(getApi())
  });

  let _commSingleton: DealerApiCommunications | undefined;
  const apiInjecterEvent = () => {
    if (!_commSingleton)
      _commSingleton = new DealerApiCommunications('', responseHandler, () => {
        //Redirect to home page, next query will force a login to occur
        window.location.href = '/login';
      });
    return _commSingleton;
  };

  //Dependency inject an api for the entire application
  setApiInjector(apiInjecterEvent);
  setCacheRegistry(() => {
    const api = getApi();
    return {
      client: new ClientCache(api),
      quoteItemConversation: new QuoteItemConversationCache(api),
      contact: new ContactReferenceCache(api),
      primaryContact: new ClientPrimaryContactCache(api),
      paymentProfile: new PaymentProfileCache(api),
      project: new ProjectCache(api),
      projectPermanentDocuments: new ProjectPermanentDocumentCache(api),
      quote: new QuoteCache(api),
      resource: new ResourcesCache(api),
      userProfile: new UserProfileCache(api),
      purchaseOrder: new PurchaseOrderCache(api),
      projectResourceLink: new ProjectResourceLinkCache(api),
      termsAndConditions: new TermsConditionsCache(api)
    };
  });

  setQuoteSupplierProviderCallback(async (_refreshList: boolean) => {
    return [
      {
        supplierId: emptyGuid,
        description: LangApplicationInitialization.loading(),
        online: false
      }
    ];
  });

  window.addEventListener('unhandledrejection', event => {
    if (event.reason.message.includes('Vaadin')) {
      return;
    }
    console.error(event.reason.stack);
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    showError(
      new ApiError({
        type: ProblemTypes.InternalError,
        title: LangApplicationInitialization.unhandledError(),
        status: 0,
        detail: event.reason.message,
        stackTrace: event.reason.stack
      }),
      () => LangApplicationInitialization.unhandledErrorInsidePromise()
    );
  });
  addV6BootLoaderEvent(afterV6BootLoader);

  runGlobalInit();

  // Initialize the page resume handler for browser lifecycle recovery
  // (tab visibility, browser wake from sleep, etc.)
  initPageResumeHandler();
}

function registerEventSourceClients() {
  //configure main event source.
  WMEventSourceClient.getInstance(undefined, {
    onlineCallback: value => {
      GlobalNotificationManager.getInstance().online = value;
      if (globalThis.dealerConfiguration.apiHost === globalThis.dealerConfiguration.v6apiHost)
        GlobalNotificationManager.getInstance('v6config').online = value;
    },
    afterOnlineEvent: () => {
      WMEventSource.afterConnect();
    },
    url: globalThis.dealerConfiguration.apiHost
  });

  // Initialize the offline overlay system to automatically show/hide based on connection state
  initOfflineOverlaySystem();

  const supplierStatusEvent = stagedExecution({
    event: async () => {
      //if we are getting this event, something was rebooted.
      v6bootloaderStale();
      globalThis.v6config = globalThis.v6config ?? {};
      globalThis.v6config.forceLoad = true;
      await getQuoteSuppliers(true);
      globalThis.v6config.forceLoad = false;
    },
    cancelToken: new EventCancellation(),
    testInterval: 250,
    threshold: 1000
  });
  //add an event listener to v6 status events
  WMEventSourceClient.getInstance().addEventListener(WMEventSource.v6configServiceStatus, supplierStatusEvent);

  // Events arriving while a refresh waits or runs are batched; every supplier they name is flushed. Both hooks
  // collect, because after a restart stagedExecution gives the first queued event to eventTriggered only. A refresh
  // whose supplier reload fails keeps its suppliers pending and is tried again after 30 seconds.
  const flushedSuppliers = new PendingCustomerCodeFlushes();
  const collectFlushedSupplier = (data: CustomerCodeFlushedEvent) => {
    if (data?.supplierId) flushedSuppliers.add(data.supplierId);
  };
  const customerCodeFlushedEvent = stagedExecution({
    eventTriggered: collectFlushedSupplier,
    eventActioned: collectFlushedSupplier,
    event: async () => {
      const done = await flushedSuppliers.run(() => getQuoteSuppliers(true), frameconfigForceFlushCaches());
      if (!done) setTimeout(() => customerCodeFlushedEvent(undefined, WMEventSource.v6CustomerCodeFlushed), 30000);
    },
    cancelToken: new EventCancellation(),
    testInterval: 250,
    threshold: 1000
  });
  WMEventSourceClient.getInstance().addEventListener(WMEventSource.v6CustomerCodeFlushed, customerCodeFlushedEvent);

  //if we are running a seperate service for v6 events bind in a 2nd listner
  if (globalThis.dealerConfiguration.apiHost !== globalThis.dealerConfiguration.v6apiHost) {
    //running a seperate server so lets do a passthrough event
    WMEventSourceClient.getInstance('v6config', {
      onlineCallback: value => {
        GlobalNotificationManager.getInstance('v6config').online = value;
      },
      afterOnlineEvent: () => {
        //WMEventSource.afterConnect();
      },
      url: globalThis.dealerConfiguration.v6apiHost
    }).addEventListener('', (data, eventName) => {
      //we dont want seperate listeners.. we will pass everything through to the main system
      WMEventSourceClient.pushEventToDefault(eventName, data);
    });
  }

  registerBranchQuoteDefaultsSSEListener();
  registerQuoteCopyCompletionMonitor();
}

async function approveTACforSupplier(supplier: QuoteSupplier) {
  const r = await getApiFactory().supplier().getSupplierTAC({ supplierId: supplier.supplierId });
  if (!r) {
    await information(
      LangApplicationInitialization.supplierTermsCannotBeRetrieved({ description: supplier.description })
    );
    return false;
  }
  const modal = new SupplierTermsAndConditionsModal(supplier, r?.supplierTermsAndConditionsOfUse);
  await modal.showModal();
  return modal.ok;
}

if (!performPageLoadLoginVerifications) console.log('missing page loader');

/**
 * perform actions here unique to after an actual login, not related to normal initialization of a user
 */

async function connectUserAfterLogin() {
  const reset = async () => {
    userDataStore.clear();

    clearCurrentUserFromSession();
    localStorage.removeItem('PAT-in-use');
    redirectToLoginPage();
  };
  const user = getCurrentUser();
  if (user !== null) {
    if (!(await verifySession())) {
      await reset();
      return;
    }

    //DO NOT MAKE ANY CALLS THAT NEED AUTHORIZATION BEFORE CONNECTING THE USER
    //Make any api calls or anything else here that are necessary to be used for the current user
    try {
      const state = await getApi().post<ConnectedState>('api/Franchisee/ConnectUser', {});
      if (!state?.connected) {
        await information(LangApplicationInitialization.loginRefused(), LangApplicationInitialization.loginRejected());
        await reset();
        return;
      }

      // Update claims/security before the shell rebinds the router.
      // Do not navigate here: routes are not registered yet (urlForName throws
      // "Route no-branches not found"). The shell parks after it mounts —
      // FranchiseeAppIndex.updateUIAspectsAfterLogin / onFranchiseeChanged.
      await userDataStore.loadCoreDetailsAfterLogin();
      // Persist the session even when the shell will park on no-branches.
      saveCurrentUserIntoSession();
    } catch (e) {
      await showDevelopmentError(e as Error);
      await reset();
    }
  } else {
    clearCurrentUserFromSession();
    localStorage.removeItem('PAT-in-use');
    userDataStore.clear();

    redirectToLoginPage();

    //we always want to login again
  }
}

let _appStartupEvents: Promise<void> | undefined;

export function getApplicationStartupEvents() {
  if (_appStartupEvents === undefined) {
    _appStartupEvents = applicationStartupEvents();
  }
  return _appStartupEvents;
}

globalThis.getApplicationStartupEvents = getApplicationStartupEvents;

async function applicationStartupEvents() {
  const log = (_msg: string) => {
    //console.log('STARTUP: ' + msg);
  };

  dealerBuildApplicationBindings();
  if (!(await dealerVerifyUser(log))) {
    window.location.search = '';
    window.location.href = '/';
  }

  await dealerPrepUserData(log);
}
